
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuizProfile } from '../../../mocks/quiz-autoconhecimento';

interface QuizResultProps {
  profile: QuizProfile;
  onRestart: () => void;
}

export default function QuizResult({ profile, onRestart }: QuizResultProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showTraits, setShowTraits] = useState(false);
  const [showRecs, setShowRecs] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setIsVisible(true), 100);
    setTimeout(() => setShowTraits(true), 600);
    setTimeout(() => setShowRecs(true), 1000);
  }, []);

  const shareText = encodeURIComponent(`Descobri que meu perfil de autoconhecimento é "${profile.title}" no quiz da Carta do Eu Futuro! ${profile.emoji}`);
  const shareUrl = encodeURIComponent(window.location.href);

  return (
    <div className="min-h-screen bg-gradient-to-b from-luxury-black via-luxury-dark to-luxury-black">
      {/* Hero do resultado */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/d841f00403c91ce7810be7bf92638359.jpg"
            alt="Resultado do quiz"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-luxury-black"></div>
        </div>

        <div className={`container mx-auto px-6 lg:px-16 relative z-10 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm border border-gold-500/20 rounded-full px-5 py-2 mb-8">
            <i className="ri-check-double-line text-gold-400 text-sm"></i>
            <span className="font-['Montserrat'] text-xs text-gold-400 tracking-wider uppercase">Quiz Completo</span>
          </div>

          <div className="text-6xl mb-6">{profile.emoji}</div>

          <h1 className="font-['Playfair_Display'] text-5xl lg:text-7xl text-white mb-3">
            <span className="font-light block">Você é</span>
            <span className="font-bold block text-gold-400 mt-2">{profile.title}</span>
          </h1>

          <p className="font-['Montserrat'] text-sm text-gold-400/60 uppercase tracking-[0.2em] mb-8">
            {profile.subtitle}
          </p>

          <p className="font-['Montserrat'] text-base text-white/45 max-w-2xl mx-auto leading-relaxed font-light">
            {profile.description}
          </p>
        </div>
      </section>

      {/* Características */}
      <section className="py-20">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-12">
              <div className="w-10 h-px bg-gradient-to-r from-transparent to-gold-500/40"></div>
              <span className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-400/50">Suas Características</span>
              <div className="w-10 h-px bg-gradient-to-l from-transparent to-gold-500/40"></div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {profile.traits.map((trait, idx) => (
                <div
                  key={idx}
                  className={`bg-white/[0.03] border border-white/8 rounded-xl p-8 text-center transition-all duration-700 hover:border-gold-500/20 hover:bg-white/[0.05] ${
                    showTraits ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                  style={{ transitionDelay: `${idx * 150}ms` }}
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gold-500/15 to-gold-600/5 flex items-center justify-center mx-auto mb-5">
                    <i className={`${trait.icon} text-2xl text-gold-400`}></i>
                  </div>
                  <h3 className="font-['Playfair_Display'] text-lg text-white mb-3">{trait.title}</h3>
                  <p className="font-['Montserrat'] text-xs text-white/35 leading-relaxed tracking-wide">{trait.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Recomendações */}
      <section className="py-16">
        <div className="container mx-auto px-6 lg:px-16">
          <div className={`max-w-2xl mx-auto transition-all duration-700 ${showRecs ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="flex items-center justify-center gap-3 mb-10">
              <div className="w-10 h-px bg-gradient-to-r from-transparent to-gold-500/40"></div>
              <span className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-400/50">Recomendações para Você</span>
              <div className="w-10 h-px bg-gradient-to-l from-transparent to-gold-500/40"></div>
            </div>

            <div className="space-y-4">
              {profile.recommendations.map((rec, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-4 bg-white/[0.02] border border-white/5 rounded-xl px-6 py-5 transition-all duration-300 hover:border-gold-500/15"
                >
                  <div className="w-8 h-8 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="font-['Montserrat'] text-xs font-bold text-gold-400">{idx + 1}</span>
                  </div>
                  <p className="font-['Montserrat'] text-sm text-white/45 leading-relaxed tracking-wide">{rec}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Compartilhar e ações */}
      <section className="py-20 pb-28">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="max-w-xl mx-auto text-center">
            <h3 className="font-['Playfair_Display'] text-xl text-white/70 mb-8">Compartilhe seu resultado</h3>

            <div className="flex items-center justify-center gap-3 mb-12">
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&quote=${shareText}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-11 h-11 rounded-xl border border-white/10 hover:border-gold-500/30 hover:bg-gold-500/10 flex items-center justify-center transition-all duration-300 cursor-pointer text-white/30 hover:text-gold-400"
              >
                <i className="ri-facebook-fill text-lg"></i>
              </a>
              <a
                href={`https://t.me/share/url?url=${shareUrl}&text=${shareText}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-11 h-11 rounded-xl border border-white/10 hover:border-gold-500/30 hover:bg-gold-500/10 flex items-center justify-center transition-all duration-300 cursor-pointer text-white/30 hover:text-gold-400"
              >
                <i className="ri-telegram-fill text-lg"></i>
              </a>
              <a
                href={`https://www.instagram.com/bia.holmes`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-11 h-11 rounded-xl border border-white/10 hover:border-gold-500/30 hover:bg-gold-500/10 flex items-center justify-center transition-all duration-300 cursor-pointer text-white/30 hover:text-gold-400"
              >
                <i className="ri-instagram-line text-lg"></i>
              </a>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="font-['Montserrat'] text-xs uppercase tracking-wider bg-gradient-to-r from-gold-500 to-gold-600 text-luxury-black px-10 py-4 rounded-lg hover:shadow-[0_0_20px_rgba(201,168,76,0.3)] transition-all duration-300 hover:scale-105 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-quill-pen-line text-base"></i>
                Escrever Minha Carta
              </button>

              <button
                onClick={onRestart}
                className="font-['Montserrat'] text-xs uppercase tracking-wider border border-white/15 text-white/40 px-8 py-4 rounded-lg hover:border-gold-500/30 hover:text-gold-400 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line text-base"></i>
                Refazer Quiz
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
